import { BehaviorSubject, catchError, EMPTY, map, Observable, Subject, switchMap, tap, timer } from "rxjs";
import { EvaService } from "src/app/core";
import { AuthenticationService } from "src/app/services/authentication.service";
import { environment } from "src/environments/environment";

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";

import { Formula, Formulas, FormulaTypes } from "../formula/models/formula.model";
import { AdminEvaSubscriptionDto } from "./dtos/admin-eva-subscription-dto";
import { EvaSubscriptionDiscount } from "./models/eva-subscription-discount.model";
import { EvaSubscriptionInvoice } from "./models/eva-subscription-invoice.model";
import { EvaSubscription, SubscriptionIntervals } from "./models/eva-subscription.model";

@Injectable({
  providedIn: "root",
})
export class EvaSubscriptionService {
  url = environment.apiUrl + "/subscriptions";
  private subscriptionSubject = new BehaviorSubject<EvaSubscription | null>(null);
  private refreshInterval = 15 * 60 * 1000; // 15 minutes

  private stripe: Stripe | null = null;

  private formulaPopupSubject = new Subject<{ formulaRecommended: string; showPrices: boolean }>();

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private router: Router,
    private evaService: EvaService,
  ) {
    // Initialiser le chargement de la souscription
    this.reloadSubscription();

    // Rafraîchir toutes les 15 minutes
    timer(0, this.refreshInterval)
      .pipe(switchMap(() => this.reloadSubscription()))
      .subscribe();
  }

  private async getStripe(): Promise<Stripe | null> {
    if (!this.stripe) {
      this.stripe = await loadStripe(environment.stripePublishableKey);
    }
    return this.stripe;
  }

  get currentSubscription$(): Observable<EvaSubscription | null> {
    return this.subscriptionSubject.asObservable();
  }

  reloadSubscription(): Observable<EvaSubscription> {
    if (!localStorage.getItem("evaJwt") || this.authenticationService.isLoggingOut) {
      return EMPTY;
    }

    const hasCompanyContext = localStorage.getItem("currentEvaCompany") || sessionStorage.getItem("currentEvaCompany");
    if (!hasCompanyContext) {
      return EMPTY;
    }

    return this.http.get<EvaSubscription>(this.url + "/current").pipe(
      tap((sub) => {
        const subscription = EvaSubscription.createInstance(sub);

        this.subscriptionSubject.next(subscription);

        // Vérifier le statut et déconnecter si INACTIVE
        if (subscription.status === "INACTIVE") {
          this.authenticationService.logout();
          this.router.navigate(["/login"]);
        }
        // Dépassement du forfait EVA_PRO (formula_overage_blocked) : pas de blocage de l'app ni de
        // modale forcée (cf. eva-layout, bandeau fermable) — la création de nouvelles factures
        // clients est bloquée côté backend (add_invoice), le reste de l'app reste accessible pour
        // permettre à la société de corriger sa situation (annuler des factures, etc.).
      }),
      catchError((error) => {
        console.error("Failed to load subscription", error);
        return EMPTY;
      }),
    );
  }

  createStripeCheckoutSession(
    email: string,
    formula: string,
    interval: string,
    subscription_id: string,
  ): Observable<any> {
    let body = {
      customer_email: email,
      formula: formula,
      interval: interval,
      subscription_id: subscription_id,
    };

    return this.http.post(this.url + "/stripe-checkout-session", body);
  }

  requestInvoice(formula: string): Observable<any> {
    return this.http.post(this.url + "/request-invoice", { formula });
  }

  async redirectToCheckout(sessionId: string) {
    const stripe = await this.getStripe();
    stripe.redirectToCheckout({ sessionId: sessionId });
  }

  stripeSuccess(): Observable<EvaSubscription> {
    return this.http.post<any>(this.url + "/stripe-success", {}).pipe(
      switchMap(() => this.reloadSubscription()),
      catchError((error) => {
        console.error("Failed to load subscription", error);
        return EMPTY;
      }),
    );
  }

  stripeUpdate(): Observable<any> {
    return this.http.post<string>(this.url + "/stripe-update", {}).pipe(
      tap((subscriptionId) => {
        this.reloadSubscription();
      }),
      catchError((error) => {
        console.error("Failed to load subscription", error);
        return EMPTY;
      }),
    );
  }

  upgradeSubscription(formula: Formula, interval: SubscriptionIntervals): Observable<any> {
    return this.http.post(this.url + "/trial-change-subscription", { formula: formula.id, interval: interval });
  }

  getAvailableFormulas(): Formula[] {
    let sub = this.subscriptionSubject.value;
    // Les montants historiques peuvent rester positifs après un retour en TRIAL.
    // Seul un abonnement ACTIVE et payant doit empêcher une nouvelle souscription.
    const isPaying = sub?.status === "ACTIVE" && ((sub.mrr ?? 0) > 0 || (sub.lifetime_amount ?? 0) > 0);
    return [
      {
        formulaType: FormulaTypes.LIGHT,
        id: Formulas.EVA_PRO,
        name: "EVA Pro",
        badge: "Toute la gestion de votre entreprise",
        customPrice: false,
        monthlyPrices: {
          CHF: 99,
        },
        yearlyPrices: {
          CHF: 990,
        },
        lifetimePrices: {},
        lifetimePricesDiscount: null,
        subPriceText: "Gratuit jusqu'à CHF 100'000 facturés, puis CHF 99.- HT/mois",
        priceHeadline: "Gratuit",
        priceHeadlineSuffix: "HT *",
        priceCaption: "*jusqu'à 100'000 CHF facturés, puis 99 CHF HT / mois",
        buttonText: sub ? "Changer de formule" : "Essayez gratuitement",
        upperFeatureText: "",
        features: ["ISSUED_INVOICES", "OFFERS", "PROJECTS", "PURCHASE_ORDERS", "ACCOUNTING", "BANKING"],
        summaryFeatures: [
          "Chantiers",
          "Offres et factures",
          "Factures fournisseurs",
          "Bons de commande",
          "Banking",
          "Exports comptables",
          "5 utilisateurs",
        ],
        detailedFeatures: [
          "Personnalisation de vos documents",
          "Envoi de vos offres et factures depuis le logiciel",
          "Factures QR-code",
          "Catalogues personnalisés : matériels, main d'oeuvre, pack",
          "Factures d'acomptes et d'avancement",
          "Suivi des règlements et relances",
          "Notes de crédit fournisseurs",
          "Planning de suivi de chantier",
          "Pièces-jointes chantier",
          "Suivi des budgets par chantier",
          "Bons de commande fournisseur",
          "Enregistrement des livraisons",
          "Bulletins de livraison",
          "Bons de travail",
          "Répertoire clients",
          "Rapprochement bancaire",
          "Émission de journaux et liasses comptables",
        ],
        image: "assets/img/formulas/elevation.svg",
        recommended: false,
        active: sub?.formula === Formulas.EVA_PRO && isPaying && !sub.historical_offer,
        available: true,
      },
      {
        formulaType: FormulaTypes.EXPERT,
        id: Formulas.EVA_360,
        name: "EVA 360",
        badge: "Votre tout-en-un",
        customPrice: true,
        monthlyPrices: {
          CHF: 299,
        },
        yearlyPrices: {},
        lifetimePrices: {},
        lifetimePricesDiscount: {},
        subPriceText: "dès CHF 299.- HT/mois, sur-mesure",
        priceLabel: "à partir de",
        priceHeadline: "299 CHF",
        priceHeadlineSuffix: "HT / mois",
        buttonText: "Prendre rendez-vous avec un expert EVA",
        upperFeatureText: "En plus de <strong>EVA Pro</strong>:",
        features: [
          "ISSUED_INVOICES",
          "OFFERS",
          "PROJECTS",
          "PURCHASE_ORDERS",
          "ACCOUNTING",
          "BANKING",
          "CONTRAT_INTERVENTIONS",
          "PLANNING",
          "HR_MANAGEMENT",
        ],
        summaryFeatures: [
          "Tout EVA Pro en illimité",
          "Timbrage des heures",
          "Application mobile terrain",
          "Planning chantiers & collaborateurs",
          "Formation et accompagnement personnel sur-mesure",
        ],
        detailedFeatures: [
          "Import de soumissions PDF, Excel, XML, SNAP",
          "Application mobile pour les collaborateurs terrain",
          "Connexion à vos outils, à l'IA en API",
          "Soldes heures supp et congés",
          "Gestion des absences, congés et paniers-repas",
          "Notes de frais et justificatifs",
          "Gestion RH des collaborateurs",
          "Suivi détaillé des chantiers (coûts, dépenses, marges)",
          "Comparaison main-d'oeuvre consommée vs facturée",
          "Récupération simplifiée des factures d'achat via une connexion à votre boîte mail",
          "Catalogues IGH, AMFIS",
          "Intégration avec vos outils comptables Winbiz, Bexio, Cresus, OfficeMaker, Abacus, Odoo…",
        ],
        optionalFeatures: [
          "Contrat d'entretien, rapports & dépannages",
          "Migration de vos données",
          "+ de 10 utilisateurs",
        ],
        image: "assets/img/formulas/installation.svg",
        recommended: true,
        active: sub?.formula === Formulas.EVA_360 && isPaying && !sub.historical_offer,
        available: true,
      },
    ];
  }
  getFormulaById(id: string): Formula {
    const formulas = this.getAvailableFormulas();
    return formulas.find((f) => f.id === id);
  }

  getInvoices(): Observable<EvaSubscriptionInvoice[]> {
    return this.http.get<EvaSubscriptionInvoice[]>(this.url + "/invoices");
  }

  modifySubscription(): Observable<any> {
    return this.http.post(this.url + "/modify", {});
  }

  upgradeFormula(formula: Formula, interval: SubscriptionIntervals) {
    if (formula.formulaType === "EXPERT") {
      window.open(
        "https://contact.eva-it.ch/meetings/franck-lauer/visio-presentation-de-loffre-installation-de-eva",
        "_blank",
      );
    } else if (formula.formulaType === "LIGHT" && this.subscriptionSubject.value?.on_trial) {
      this.upgradeSubscription(formula, interval).subscribe(
        (result) => {
          this.evaService.showSuccess("Votre formule a été mise à jour avec succès");
          window.location.reload();
        },
        (error) => {
          this.evaService.showError(
            "Une erreur est survenue lors de la mise à jour de votre formule. Veuillez contacter support@eva-it.ch",
            "onFormulaSelected",
            error,
            true,
          );
        },
      );
    } else {
      this.modifySubscription().subscribe((result) => {
        // open the checkout page on the same page
        window.open(result.link, "_self");
      });
    }
  }

  activateSubscription(formula: string, interval: "MONTHLY" | "YEARLY" | "LIFETIME") {
    this.evaService.trackEvent("subscription_click_activate");

    let user_id = this.authenticationService.currentUserValue.user_id;

    this.createStripeCheckoutSession(
      user_id,
      formula,
      interval,
      this.subscriptionSubject.value.stripe_subscription_id,
    ).subscribe({
      next: (session) => {
        this.redirectToCheckout(session.sessionId);
      },
      error: (error) => {
        console.error("Failed to create checkout session", error);
        this.evaService.showError(
          "Une erreur est survenue lors de la création de la session de paiement. Veuillez contacter le support à support@eva-it.ch",
          "activateLicence",
          error,
        );
      },
    });
  }

  logout() {
    this.subscriptionSubject.next(null);
  }

  getAdminListSubcriptions(): Observable<AdminEvaSubscriptionDto[]> {
    return this.http
      .get<AdminEvaSubscriptionDto[]>(this.url + "/admin-list")
      .pipe(map(AdminEvaSubscriptionDto.createInstances));
  }

  sendPendingInitialization(): Observable<{ status: string; email: string }> {
    return this.http.post<{ status: string; email: string }>(this.url + "/current/pending-initialization", {});
  }

  patchSubscription(subscription_id: string, body: any): Observable<any> {
    return this.http.patch(this.url + "/" + subscription_id, body);
  }

  getCurrentDiscount(): Observable<EvaSubscriptionDiscount> {
    return this.http
      .get<EvaSubscriptionDiscount>(this.url + "/current-discount", {})
      .pipe(map(EvaSubscriptionDiscount.createInstance));
  }

  showFormulaPopup(formulaRecommended: string = null, showPrices: boolean = false): void {
    this.formulaPopupSubject.next({ formulaRecommended, showPrices });
  }

  getFormulaPopupObservable(): Observable<{ formulaRecommended: string; showPrices: boolean }> {
    return this.formulaPopupSubject.asObservable();
  }

  getAdminStatsHistory(
    months: number = 12,
    groupBy: "week" | "month" = "week",
    year: number = null,
  ): Observable<any[]> {
    let params: any = { group_by: groupBy };
    if (year) {
      params.year = year.toString();
    } else {
      params.months = months.toString();
    }
    return this.http.get<any[]>(this.url + "/admin-stats-history", { params });
  }

  getAdminTrialStats(months: number = 12, groupBy: "week" | "month" = "month"): Observable<any[]> {
    return this.http.get<any[]>(this.url + "/admin-trial-stats", {
      params: { months: months.toString(), group_by: groupBy },
    });
  }

  getAdminConversionDaysStats(months: number = 12): Observable<any[]> {
    return this.http.get<any[]>(this.url + "/admin-conversion-days-stats", {
      params: { months: months.toString() },
    });
  }
}
